/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import ExcelJS from 'exceljs';
import { JobRun, JobRunItem } from './jobs.service';

/**
 * Read-only view of one run report — the JSON the sidecar writes under
 * Data Dictionary/DMS Jobs/Runs. Shows the exact AFTS query, the counters,
 * the per-file item list (name, location, size, outcome) and every error.
 * "Export Excel" writes the same data to a two-sheet .xlsx for sharing.
 */
@Component({
  selector: 'app-job-run-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './job-run-dialog.component.html',
  styleUrl: './job-run-dialog.component.scss'
})
export class JobRunDialogComponent {
  private readonly ref = inject(MatDialogRef<JobRunDialogComponent>);
  private readonly datePipe = inject(DatePipe);
  private readonly translate = inject(TranslateService);

  readonly run: JobRun = inject(MAT_DIALOG_DATA).run;

  dateLabel(iso?: string | null): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm:ss') ?? '—');
  }

  /** Bytes → "1.4 MB" — compact for the files table. */
  sizeLabel(size?: number | null): string {
    if (size === null || size === undefined) {
      return '—';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = size;
    let u = 0;
    while (v >= 1024 && u < units.length - 1) {
      v /= 1024;
      u++;
    }
    return `${v.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
  }

  statusLabel(item: JobRunItem): string {
    return this.translate.instant(`DMS.JOBS.STATUS_${item.status.toUpperCase()}`) || item.status;
  }

  statusIcon(item: JobRunItem): string {
    switch (item.status) {
      case 'processed':
        return 'check_circle';
      case 'skipped':
        return 'block';
      default:
        return 'error';
    }
  }

  /** True when the report's item list was capped below the matched count. */
  get itemsCapped(): boolean {
    return (this.run.items?.length ?? 0) < this.run.processed + (this.run.errors?.length ?? 0);
  }

  /** Two sheets — run summary + the per-file list — as one .xlsx download. */
  async exportXlsx(): Promise<void> {
    const t = (key: string, params?: Record<string, unknown>) => this.translate.instant(key, params);
    const wb = new ExcelJS.Workbook();
    wb.creator = 'ACA DMS Jobs';
    wb.created = new Date();

    const meta = wb.addWorksheet('Run');
    meta.columns = [{ width: 16 }, { width: 100 }];
    meta.addRows([
      [t('DMS.JOBS.COL_JOB'), this.run.jobName],
      [t('DMS.JOBS.COL_ACTION'), t(`DMS.JOBS.ACT_${(this.run.action || '').toUpperCase()}`)],
      [t('DMS.JOBS.COL_TRIGGER'), this.run.triggeredBy],
      [t('DMS.JOBS.DRYRUN'), this.run.dryRun ? '✓' : ''],
      [t('DMS.JOBS.COL_STARTED'), this.dateLabel(this.run.startedAt)],
      [t('DMS.JOBS.COL_FINISHED'), this.dateLabel(this.run.finishedAt)],
      [t('DMS.JOBS.COL_DURATION'), `${(this.run.durationMs / 1000).toFixed(1)} s`],
      [t('DMS.JOBS.COL_MATCHED'), this.run.matched],
      [t('DMS.JOBS.COL_PROCESSED'), this.run.processed],
      [t('DMS.JOBS.COL_ERRORS'), this.run.errors?.length ?? 0],
      [t('DMS.JOBS.COL_EMAILED'), this.run.emailed?.join(', ') ?? ''],
      [t('DMS.JOBS.RUN_QUERY'), this.run.query ?? '']
    ]);
    meta.getRow(1).font = { bold: true };

    const files = wb.addWorksheet('Files');
    files.columns = [
      { header: t('DMS.JOBS.F_NAME'), key: 'name', width: 42 },
      { header: t('DMS.JOBS.RUN_LOCATION'), key: 'path', width: 62 },
      { header: t('DMS.JOBS.COL_SIZE'), key: 'size', width: 14 },
      { header: 'MIME', key: 'mime', width: 24 },
      { header: t('DMS.JOBS.F_MODIFIED'), key: 'modified', width: 20 },
      { header: t('DMS.JOBS.RUN_STATUS'), key: 'status', width: 14 },
      { header: t('DMS.JOBS.COL_ERRORS'), key: 'error', width: 46 }
    ];
    files.getRow(1).font = { bold: true };
    for (const item of this.run.items ?? []) {
      files.addRow({
        name: item.name,
        path: item.path,
        size: item.size ?? '',
        mime: item.mimeType ?? '',
        modified: this.dateLabel(item.modified),
        status: this.statusLabel(item),
        error: item.error ?? ''
      });
    }

    const stamp = (this.run.startedAt || '').replace(/[:T]/g, '').slice(0, 14) || Date.now().toString();
    const slug = (this.run.jobName || 'job').replace(/[\\/:*?"<>|]+/g, '-').slice(0, 60);
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `run-report-${slug}-${stamp}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  close(): void {
    this.ref.close();
  }
}
